import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { isNavigationFailure, NavigationFailureType } from 'vue-router';
import router from '@/router';
// beautySlug is no longer used for project name slugs on frontend

dayjs.extend(utc);

let pendingGotoPath = null;
// timed alerts by alertId; hovering an alert drops its timer (holdAlert)
const alertTimers = {};

function isDuplicatedNavigation(error) {
  return error?.name === 'NavigationDuplicated'
    || error?.message?.includes('Avoided redundant navigation')
    || error?.type === 16;
}

async function openPendingPath() {
  if (!pendingGotoPath) {
    return;
  }

  const path = pendingGotoPath;
  pendingGotoPath = null;

  try {
    await router.isReady();
    return await router.push(path);
  } catch (e) {
    if (isDuplicatedNavigation(e)) {
      return;
    }
    console.error('Failed to open deep link path', path, e);
  }
}

export default {
  namespaced: true,
  state: {
    nowUTC: dayjs.utc(),
    permanentAlerts: {
    },
    mobileMode: window.innerWidth <= 600,
    tabletMode: window.innerWidth > 1024 && window.innerWidth <= 1200,
    tabletSmallMode: window.innerWidth > 600 && window.innerWidth <= 1024,
    syncing: false,
    requireAuth: false,
    openedDialog: null, // e.g. 'NotificationWrapper', 'CreateTask'
    pageScrolled: false,
    recordingActive: false, // global screen recording state
    recordingPaused: false,
  },
  mutations: {
    setRequireAuth(state, data) {
      state.requireAuth = data;
    },
    setSyncing(state, data) {
      state.syncing = data;
    },
    setCurrentTime(state, data) {
      state.nowUTC = data;
    },
    setPageScrolled(state, data) {
      state.pageScrolled = data;
    },
    setMobileMode(state, data) {
      state.mobileMode = data;
    },
    setTabletMode(state, data) {
      state.tabletMode = data;
    },
    setTabletSmallMode(state, data) {
      state.tabletSmallMode = data;
    },
    showAlert(state, alertData) {
      console.log('📢 Setting permanent alert', alertData);
      if (!alertData.alertId) {
        alertData.alertId = `alert_${Date.now()}`;
      }
      if (!alertData.type) {
        alertData.type = 'warning';
      }
      state.permanentAlerts[alertData.alertId] = alertData;
      // the same alertId shown again replaces the alert, so its old timer must not remove the new one
      clearTimeout(alertTimers[alertData.alertId]);
      if (alertData.timeout) {
        alertTimers[alertData.alertId] = setTimeout(() => {
          delete alertTimers[alertData.alertId];
          delete state.permanentAlerts[alertData.alertId];
        }, alertData.timeout * 1000);
      }
    },
    // hovering a timed alert means reading it: it stays until the user closes it with the X
    holdAlert(state, { alertId }) {
      const alert = state.permanentAlerts[alertId];
      if (!alert.timeout) {
        return; // an alert without timeout (e.g. connection lost) is not the user's to close
      }
      clearTimeout(alertTimers[alertId]);
      delete alertTimers[alertId];
      alert.timeout = 0;
      alert.closable = true;
    },
    clearAlert(state, { alertId }) {
      console.log('🧹 Clearing alert', alertId);
      delete state.permanentAlerts[alertId];
    },
    setOpenedDialog(state, data) {
      state.openedDialog = data;
    },
    setRecordingActive(state, active) {
      state.recordingActive = active;
    },
    setRecordingPaused(state, paused) {
      state.recordingPaused = paused;
    },
  },
  actions: {
    setMobileModeAction({ commit }, mobileMode) {
      commit('setMobileMode', mobileMode);
    },
    setTabletModeAction({ commit }, tabletMode) {
      commit('setTabletMode', tabletMode);
    },
    setTabletSmallModeAction({ commit }, tabletSmallMode) {
      commit('setTabletSmallMode', tabletSmallMode);
    },
    async gotoPath({ dispatch, rootState }, path) {
      if (!path) {
        return;
      }
      pendingGotoPath = path;
      const failure = await openPendingPath();
      if (isNavigationFailure(failure, NavigationFailureType.duplicated)) {
        // The app is already on this task, so the route did not change and TaskDialog did not
        // re-mount. Normally initTask starts tracking on mount; here we have to do it ourselves.
        dispatch('task/startTracking', { task: rootState.project.taskUnderEdit }, { root: true });
      }
    },
    async flushPendingGotoPath() {
      await openPendingPath();
    },
  },
};
